import { useMutation, useQuery } from "@apollo/client";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Autocomplete, Box, Button, CircularProgress, TextField, Typography } from "@mui/material";
import { Dialog } from "components/Dialog";
import { gql } from "generated-graphql";
import { InsightsCohort } from "generated-graphql/graphql";
import { useEffect, useState } from "react";

const INSIGHTS_COHORTS = gql(`
  query InsightsCohorts($tenantId: String!) {
    insightsCohorts(tenantId: $tenantId) {
      tenantId
      id
      name
      nameSearches
      domainSearches
    }
  }
`);

export const CREATE_INSIGHTS_COHORT = gql(`
  mutation CreateInsightsCohort(
    $tenantId: String!
    $name: String!
    $nameSearches: [String!]!
    $domainSearches: [String!]!
  ) {
    createInsightsCohort(
      tenantId: $tenantId
      name: $name
      nameSearches: $nameSearches
      domainSearches: $domainSearches
    ) {
      id
      name
      nameSearches
      domainSearches
    }
  }
`);

export const UPDATE_INSIGHTS_COHORT = gql(`
  mutation UpdateInsightsCohort(
    $id: String!
    $name: String!
    $nameSearches: [String!]!
    $domainSearches: [String!]!
  ) {
    updateInsightsCohort(
      id: $id
      name: $name
      nameSearches: $nameSearches
      domainSearches: $domainSearches
    ) {
      id
      name
      nameSearches
      domainSearches
    }
  }
`);

export const DELETE_INSIGHTS_COHORT = gql(`
  mutation DeleteInsightsCohort($id: String!) {
    deleteInsightsCohort(id: $id) {
      id
    }
  }
`);

export default function CohortSelector({
  tenantId,
  onComparisonSubmit,
  loading,
  selectedCohortIds,
}: {
  tenantId: string;
  onComparisonSubmit: (selectedCohortIds: string[]) => void;
  loading: boolean;
  selectedCohortIds: string[];
}) {
  const [selectedCohort, setSelectedCohort] = useState<InsightsCohort[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteInsightsCohort] = useMutation(DELETE_INSIGHTS_COHORT);
  const [selectedCohortForEdit, setSelectedCohortForEdit] = useState<InsightsCohort | null>(null);

  const {
    data: insightsCohortsData,
    loading: insightsCohortsLoading,
    error: insightsCohortsError,
    refetch: refetchInsightsCohorts,
  } = useQuery(INSIGHTS_COHORTS, {
    variables: {
      tenantId,
    },
  });

  useEffect(() => {
    if (insightsCohortsData && selectedCohortIds) {
      const selectedCohorts = insightsCohortsData.insightsCohorts.filter((cohort) =>
        selectedCohortIds.includes(cohort.id)
      );
      setSelectedCohort(selectedCohorts);
    }
  }, [insightsCohortsData, selectedCohortIds]);

  const [createInsightsCohort] = useMutation(CREATE_INSIGHTS_COHORT);
  const [updateInsightsCohort] = useMutation(UPDATE_INSIGHTS_COHORT);

  const handleOpenModal = (cohort: InsightsCohort | null) => {
    setSelectedCohortForEdit(cohort);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedCohortForEdit(null);
    setModalOpen(false);
  };

  const handleSaveCohort = async (cohortData: InsightsCohort) => {
    if (selectedCohortForEdit) {
      await updateInsightsCohort({
        variables: { ...cohortData, id: selectedCohortForEdit.id },
      });
    } else {
      const result = await createInsightsCohort({
        variables: { ...cohortData, tenantId },
      });
      const newCohort = result.data?.createInsightsCohort;
      if (newCohort) {
        const refetchResult = await refetchInsightsCohorts();
        const allCohorts = refetchResult.data?.insightsCohorts || [];
        const prevIds = selectedCohort.map((c) => c.id);
        const idsToSelect = new Set([...prevIds, newCohort.id]);
        setSelectedCohort(allCohorts.filter((c: InsightsCohort) => idsToSelect.has(c.id)));
      }
    }
  };

  const handleDeleteCohort = async (cohortId: string) => {
    await deleteInsightsCohort({
      variables: { id: cohortId },
    });
    refetchInsightsCohorts();
    setSelectedCohort(selectedCohort.filter((c) => c.id !== cohortId));
  };

  const handleComparisonSubmit = () => {
    const selectedCohortIds = selectedCohort.map((cohort) => cohort.id);
    onComparisonSubmit(selectedCohortIds);
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
      <Autocomplete
        multiple
        options={insightsCohortsData?.insightsCohorts || []}
        getOptionLabel={(option) => option.name}
        value={selectedCohort}
        onChange={(event, newValue) => {
          const createNewOption = newValue.find((option) => option.id === "create-new");
          if (createNewOption) {
            newValue = newValue.filter((option) => option.id !== "create-new");
            handleOpenModal(null);
          }
          setSelectedCohort(newValue as InsightsCohort[]);
        }}
        filterOptions={(options, state) => {
          const filtered = options.filter((option) =>
            option.name.toLowerCase().includes(state.inputValue.toLowerCase())
          );
          return [
            ...filtered,
            {
              id: "create-new",
              name: "Create New",
              tenantId: "",
              nameSearches: [],
              domainSearches: [],
            },
          ];
        }}
        renderOption={(props, option) => (
          <Box component="li" {...props} sx={{ display: "flex", alignItems: "center" }}>
            {option.name}
            <Box sx={{ marginLeft: "auto" }}>
              {option.id === "create-new" ? (
                <AddIcon
                  fontSize="small"
                  sx={{ ml: 1, cursor: "pointer" }}
                  onClick={() => handleOpenModal(null)}
                />
              ) : (
                <EditIcon
                  fontSize="small"
                  sx={{ ml: 1, cursor: "pointer" }}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleOpenModal(option);
                  }}
                />
              )}
              <DeleteIcon
                fontSize="small"
                sx={{ ml: 1, cursor: "pointer" }}
                onClick={(event) => {
                  event.stopPropagation();
                  handleDeleteCohort(option.id);
                }}
              />
            </Box>
          </Box>
        )}
        renderInput={(params) => <TextField {...params} label="Cohort" />}
        sx={{ flexGrow: 1, mr: 2 }}
      />
      <Button
        variant="contained"
        onClick={handleComparisonSubmit}
        startIcon={loading ? <CircularProgress size={24} /> : null}
        disabled={loading || selectedCohort.length === 0}
      >
        Compare
      </Button>
      <CohortModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveCohort}
        cohort={selectedCohortForEdit}
        selectedCohort={selectedCohort}
        setSelectedCohort={setSelectedCohort}
      />
    </Box>
  );
}

function CohortModal({
  open,
  onClose,
  onSave,
  cohort,
  selectedCohort,
  setSelectedCohort,
}: {
  open: boolean;
  onClose: () => void;
  onSave: (cohortData: InsightsCohort) => void;
  cohort: InsightsCohort | null;
  selectedCohort: InsightsCohort[];
  setSelectedCohort: (cohorts: InsightsCohort[]) => void;
}) {
  const [name, setName] = useState(cohort?.name || "");
  const [nameSearches, setNameSearches] = useState(cohort?.nameSearches || []);
  const [domainSearches, setDomainSearches] = useState(cohort?.domainSearches || []);

  useEffect(() => {
    setName(cohort?.name || "");
    setNameSearches(cohort?.nameSearches || []);
    setDomainSearches(cohort?.domainSearches || []);
  }, [cohort]);

  const handleSave = () => {
    const cohortData: InsightsCohort = {
      ...cohort,
      name,
      nameSearches,
      domainSearches,
      id: cohort?.id || "",
      tenantId: cohort?.tenantId || "",
    };
    onSave(cohortData);
    if (selectedCohort.some((c) => c.id === cohortData.id)) {
      setSelectedCohort(selectedCohort.map((c) => (c.id === cohortData.id ? cohortData : c)));
    } else {
      setSelectedCohort([...selectedCohort, cohortData]);
    }
    setName("");
    setNameSearches([]);
    setDomainSearches([]);
    onClose();
  };

  // Check if all fields are filled and if the form is unchanged (only for edit)
  const isUnchanged =
    (cohort?.name || "") === name &&
    JSON.stringify(cohort?.nameSearches || []) === JSON.stringify(nameSearches) &&
    JSON.stringify(cohort?.domainSearches || []) === JSON.stringify(domainSearches);

  const isSaveDisabled =
    !name.trim() ||
    nameSearches.every((s) => !s.trim()) ||
    domainSearches.every((s) => !s.trim()) ||
    (cohort ? isUnchanged : false);

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="xs"
      onClose={onClose}
      title={cohort ? "Edit Cohort" : "Create Cohort"}
      primaryActionButtonProps={{
        onClick: handleSave,
        disabled: isSaveDisabled,
      }}
    >
      <TextField
        label="Cohort Title"
        value={name}
        onChange={(e) => setName(e.target.value)}
        fullWidth
        margin="normal"
        required
      />
      <Typography variant="h6">Search Terms</Typography>
      <TextField
        label="Name Searches"
        value={nameSearches.join(",")}
        onChange={(e) => setNameSearches(e.target.value.split(","))}
        fullWidth
        margin="normal"
        required
      />
      <TextField
        label="Domain Searches"
        value={domainSearches.join(",")}
        onChange={(e) => setDomainSearches(e.target.value.split(","))}
        fullWidth
        margin="normal"
        required
      />
    </Dialog>
  );
}
